import { useEffect, useMemo, useState } from 'react'
import { useApolloClient } from '@apollo/client'
import { GET_LESSON_AVAILABLE_TIMES } from './coach-profile-booking-widget.query'
import {
  AVAILABLE_TIMES_QUERY_LIMIT,
  getBookingHorizonUtcRange,
} from './coach-profile-booking-widget.utils'

const emptyAvailabilityState = (loading = false) => ({
  loading,
  byLessonType: {},
  error: null,
})

/**
 * Coach Tools availability keyed by lesson type id (prefetch for coach-wide
 * location options and per-offering slot UI).
 */
export function useBookingAvailabilityByLessonType({
  lessonTypeIds = [],
  scheduleUrl,
  timezone,
}) {
  const client = useApolloClient()
  const horizonRange = useMemo(
    () => getBookingHorizonUtcRange(timezone),
    [timezone],
  )
  const lessonTypeKey = lessonTypeIds.join(',')
  const shouldFetch = Boolean(scheduleUrl && lessonTypeIds.length && horizonRange)
  const [state, setState] = useState(() => (
    emptyAvailabilityState(shouldFetch)
  ))

  useEffect(() => {
    if (!shouldFetch) {
      setState(emptyAvailabilityState(false))
      return undefined
    }

    let cancelled = false
    setState(emptyAvailabilityState(true))

    Promise.allSettled(lessonTypeIds.map((id) => client.query({
      query: GET_LESSON_AVAILABLE_TIMES,
      variables: {
        id,
        startDateTime: horizonRange.startDateTime,
        endDateTime: horizonRange.endDateTime,
        limit: AVAILABLE_TIMES_QUERY_LIMIT,
      },
      context: { clientName: 'coach-tools' },
      fetchPolicy: 'network-only',
    })))
      .then((results) => {
        if (cancelled) return
        const byLessonType = {}
        const errors = []
        lessonTypeIds.forEach((id, index) => {
          const result = results[index]
          if (result.status === 'fulfilled') {
            byLessonType[id] = result.value?.data?.lessonTypes?.[0] || null
          } else {
            errors.push(result.reason)
          }
        })
        setState({
          loading: false,
          byLessonType,
          error: errors[0] || null,
        })
      })

    return () => {
      cancelled = true
    }
  }, [
    client,
    horizonRange,
    lessonTypeKey,
    scheduleUrl,
    shouldFetch,
  ])

  return state
}
